/**
 * MySchedule.jsx
 * 
 * Employee view of their upcoming shifts.
 * Shows published rota for the current and upcoming weeks.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Coffee as BreakIcon,
  BeachAccess as HolidayIcon,
  EventAvailable as ShiftIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

const MySchedule = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scheduleData, setScheduleData] = useState(null);
  const [error, setError] = useState(null);
  const [weeksToShow, setWeeksToShow] = useState(3);

  useEffect(() => {
    loadSchedule();
  }, [weeksToShow]);

  const loadSchedule = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/rota/my-schedule?weeks=${weeksToShow}`);
      setScheduleData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load schedule:', err);
      if (err.response?.status === 404) {
        setError('No employee record found for your account. Please contact your manager.');
      } else {
        setError('Failed to load your schedule. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupShiftsByWeek = (shifts) => {
    if (!shifts?.length) return {};
    
    const grouped = {};
    shifts.forEach(shift => {
      const date = new Date(shift.date);
      const monday = new Date(date);
      monday.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!grouped[weekKey]) {
        grouped[weekKey] = [];
      }
      grouped[weekKey].push(shift);
    });
    
    return grouped;
  };

  const formatWeekRange = (weekStart) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    
    const options = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)}`;
  };

  const isCurrentWeek = (weekStart) => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    return weekStart === monday.toISOString().split('T')[0];
  };

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  const calculateHours = (startTime, endTime, breakDuration) => {
    if (!startTime || !endTime) return 0;
    
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    
    const totalMinutes = endMinutes - startMinutes - (breakDuration || 0);
    return (totalMinutes / 60).toFixed(1);
  };

  const getTotalHoursForWeek = (shifts) => {
    return shifts.reduce((total, shift) => {
      return total + parseFloat(calculateHours(shift.start_time, shift.end_time, shift.break_duration));
    }, 0).toFixed(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const groupedShifts = groupShiftsByWeek(scheduleData?.shifts || []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
          color: 'white',
          p: 4,
          borderRadius: 0
        }}
      >
        <Container maxWidth="md">
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/time')}
            sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Back to Time & Attendance
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            My Schedule
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {scheduleData?.employee?.name ? `Hi ${scheduleData.employee.name.split(' ')[0]}! Here's your upcoming schedule.` : 'View your upcoming shifts'}
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Summary Card */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Upcoming Shifts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {scheduleData?.period_start && scheduleData?.period_end && (
                        `${new Date(scheduleData.period_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(scheduleData.period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ textAlign: 'center', px: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {scheduleData?.total_shifts || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Shifts
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', px: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                        {scheduleData?.holidays?.length || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Holidays
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Holiday Notice */}
            {scheduleData?.holidays?.length > 0 && (
              <Alert 
                icon={<HolidayIcon />} 
                severity="info" 
                sx={{ mb: 3 }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  Upcoming Holidays
                </Typography>
                {scheduleData.holidays.map((holiday, idx) => (
                  <Typography key={idx} variant="body2">
                    {new Date(holiday.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {holiday.start_date !== holiday.end_date && ` - ${new Date(holiday.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    {` (${holiday.days} day${holiday.days > 1 ? 's' : ''})`}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* Schedule by Week */}
            {Object.keys(groupedShifts).length > 0 ? (
              Object.entries(groupedShifts)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([weekStart, shifts]) => (
                  <Card 
                    key={weekStart} 
                    elevation={0} 
                    sx={{ 
                      border: isCurrentWeek(weekStart) ? '2px solid #00bcd4' : '1px solid #e2e8f0',
                      mb: 3 
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon color="action" />
                          <Typography variant="h6" fontWeight="bold">
                            Week of {formatWeekRange(weekStart)}
                          </Typography>
                          {isCurrentWeek(weekStart) && (
                            <Chip label="This Week" size="small" color="primary" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {getTotalHoursForWeek(shifts)} hrs total
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {shifts
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .map((shift) => (
                            <Paper
                              key={shift.id}
                              sx={{
                                p: 2,
                                bgcolor: isToday(shift.date) ? '#00bcd410' : '#f8fafc',
                                border: isToday(shift.date) ? '1px solid #00bcd4' : '1px solid #e2e8f0',
                                borderLeft: `4px solid ${shift.color || '#667eea'}`,
                                borderRadius: 2
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {shift.day_name}
                                    </Typography>
                                    {isToday(shift.date) && (
                                      <Chip label="Today" size="small" color="primary" sx={{ height: 20 }} />
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(shift.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                                  </Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                  <Chip
                                    label={shift.template_name || 'Shift'}
                                    size="small"
                                    sx={{
                                      bgcolor: (shift.color || '#667eea') + '20',
                                      color: shift.color || '#667eea',
                                      fontWeight: 600,
                                      mb: 1
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" fontWeight="600">
                                      {shift.start_time} - {shift.end_time}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {calculateHours(shift.start_time, shift.end_time, shift.break_duration)} hours
                                    {shift.break_duration > 0 && (
                                      <span> ({shift.break_duration}m break)</span>
                                    )}
                                  </Typography>
                                </Box>
                              </Box>

                              {shift.notes && (
                                <Alert severity="info" sx={{ mt: 2, py: 0 }}>
                                  <Typography variant="caption">{shift.notes}</Typography>
                                </Alert>
                              )}
                            </Paper>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))
            ) : (
              <Card elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <ScheduleIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Shifts Scheduled
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your manager hasn't published the rota yet, or you don't have any shifts scheduled for the upcoming weeks.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Load More */}
            {scheduleData?.total_shifts > 0 && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setWeeksToShow(prev => prev + 2)}
                  disabled={weeksToShow >= 8}
                >
                  Show More Weeks
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default MySchedule;